import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Trash2, Plus, Car } from 'lucide-react';
import { 
  VehicleDamageReport, 
  DamagePoint, 
  saveDamageReport, 
  DAMAGE_TYPES, 
  SEVERITY_LEVELS 
} from '../lib/damageReports';
import { useBusiness } from '../contexts/BusinessContext';

interface VehicleDamageReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  existingReport?: VehicleDamageReport | null;
  vehicleInfo?: {
    make?: string;
    model?: string;
    year?: string;
    color?: string;
    licensePlate?: string;
  };
  onSave: (report: VehicleDamageReport) => void;
}

interface DamagePointWithTempId extends Omit<DamagePoint, 'id'> {
  id: string;
  isTemp?: boolean;
}

export default function VehicleDamageReportModal({
  isOpen,
  onClose,
  bookingId,
  existingReport,
  vehicleInfo,
  onSave
}: VehicleDamageReportModalProps) {
  
  const { selectedBusiness } = useBusiness();

  // Debug logging for modal state
  useEffect(() => {
    console.log('🚗 [DAMAGE MODAL] State changed:', { isOpen, bookingId });
  }, [isOpen, bookingId]);
  const [damagePoints, setDamagePoints] = useState<DamagePointWithTempId[]>([]);
  const [vehicleDetails, setVehicleDetails] = useState({
    make: vehicleInfo?.make || '',
    model: vehicleInfo?.model || '',
    year: vehicleInfo?.year || '',
    color: vehicleInfo?.color || '',
    licensePlate: vehicleInfo?.licensePlate || ''
  });
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const [isAddingPoint, setIsAddingPoint] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const vehicleDiagramRef = useRef<HTMLDivElement>(null);

  // Initialize form data when modal opens or existing report changes
  useEffect(() => {
    if (isOpen) {
      if (existingReport) {
        setDamagePoints(existingReport.damage_points || []);
        setVehicleDetails({
          make: existingReport.vehicle_make || vehicleInfo?.make || '',
          model: existingReport.vehicle_model || vehicleInfo?.model || '',
          year: existingReport.vehicle_year || vehicleInfo?.year || '',
          color: existingReport.vehicle_color || vehicleInfo?.color || '',
          licensePlate: existingReport.license_plate || vehicleInfo?.licensePlate || ''
        });
        setAdditionalNotes(existingReport.additional_notes || '');
      } else {
        // Reset form for new report
        setDamagePoints([]);
        setVehicleDetails({
          make: vehicleInfo?.make || '',
          model: vehicleInfo?.model || '',
          year: vehicleInfo?.year || '',
          color: vehicleInfo?.color || '',
          licensePlate: vehicleInfo?.licensePlate || ''
        });
        setAdditionalNotes('');
      }
      setSelectedPointId(null);
      setIsAddingPoint(false);
    }
  }, [isOpen, existingReport, vehicleInfo]);

  const handleVehicleDiagramClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isAddingPoint || !vehicleDiagramRef.current) return;

    const rect = vehicleDiagramRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newPoint: DamagePointWithTempId = {
      id: `temp_${Date.now()}`,
      x,
      y,
      damageType: 'scratch',
      severity: 'minor',
      notes: '',
      createdAt: new Date().toISOString(),
      isTemp: true
    };

    setDamagePoints(prev => [...prev, newPoint]);
    setSelectedPointId(newPoint.id);
    setIsAddingPoint(false);
  };

  const updateDamagePoint = (pointId: string, updates: Partial<DamagePointWithTempId>) => {
    setDamagePoints(prev => 
      prev.map(point => 
        point.id === pointId 
          ? { ...point, ...updates, isTemp: false }
          : point
      )
    );
  };

  const removeDamagePoint = (pointId: string) => {
    setDamagePoints(prev => prev.filter(point => point.id !== pointId));
    if (selectedPointId === pointId) {
      setSelectedPointId(null);
    }
  };

  const handleSave = async () => {
    if (!selectedBusiness) return;

    setIsSaving(true);
    try {
      // Convert temp IDs to proper UUIDs for saving
      const finalDamagePoints = damagePoints.map(point => ({
        ...point,
        id: point.isTemp ? crypto.randomUUID() : point.id,
        isTemp: undefined
      }));

      const reportData: Partial<VehicleDamageReport> = {
        business_id: selectedBusiness.id,
        booking_id: bookingId,
        vehicle_make: vehicleDetails.make || undefined,
        vehicle_model: vehicleDetails.model || undefined,
        vehicle_year: vehicleDetails.year || undefined,
        vehicle_color: vehicleDetails.color || undefined,
        license_plate: vehicleDetails.licensePlate || undefined,
        damage_points: finalDamagePoints,
        additional_notes: additionalNotes || undefined
      };

      const savedReport = await saveDamageReport(reportData);
      onSave(savedReport);
      onClose();
    } catch (error) {
      console.error('Error saving damage report:', error);
      alert('Failed to save damage report. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedPoint = damagePoints.find(point => point.id === selectedPointId);
  const damageTypeColor = selectedPoint ? 
    DAMAGE_TYPES.find(type => type.value === selectedPoint.damageType)?.color || '#a0a0a0' :
    '#a0a0a0';

  if (!isOpen) return null;

  console.log('🚗 [DAMAGE MODAL] Rendering modal with Portal');

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full h-full sm:max-w-6xl sm:w-full sm:max-h-[90vh] sm:h-auto overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Car className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            <h2 className="text-lg sm:text-xl font-semibold">
              {existingReport ? 'Edit Damage Report' : 'Add Damage Report'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
              
              {/* Vehicle Diagram */}
              <div className="lg:col-span-2">
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                  <h3 className="text-base sm:text-lg font-medium">Vehicle Diagram</h3>
                  <button
                    onClick={() => setIsAddingPoint(!isAddingPoint)}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 sm:py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                      isAddingPoint
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    <Plus className="h-4 w-4" />
                    <span>{isAddingPoint ? 'Cancel' : 'Add Damage Point'}</span>
                  </button>
                </div>

                {/* Vehicle Diagram Container */}
                <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div
                    ref={vehicleDiagramRef}
                    className={`relative w-full h-[300px] sm:h-[400px] lg:h-[500px] ${
                      isAddingPoint ? 'cursor-crosshair' : 'cursor-default'
                    }`}
                    onClick={handleVehicleDiagramClick}
                  >
                    {/* Professional Vehicle Damage Report - Using User's Image */}
                    <img
                      src="/cardiagram.png.jpg"
                      alt="Vehicle Damage Report Diagram"
                      className="w-full h-full object-contain"
                      draggable={false}
                    />

                    {/* Damage Points */}
                    {damagePoints.map((point, index) => {
                      const typeConfig = DAMAGE_TYPES.find(type => type.value === point.damageType);
                      const isSelected = selectedPointId === point.id;
                      const pointNumber = index + 1;
                      
                      return (
                        <div
                          key={point.id}
                          className={`absolute w-7 h-7 sm:w-6 sm:h-6 rounded-full border-2 cursor-pointer transform -translate-x-3 -translate-y-3 flex items-center justify-center text-white text-xs font-bold touch-manipulation ${
                            isSelected ? 'border-white scale-125 z-10 shadow-lg' : 'border-gray-800'
                          }`}
                          style={{
                            left: `${point.x}%`,
                            top: `${point.y}%`,
                            backgroundColor: typeConfig?.color || '#a0a0a0'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPointId(point.id);
                          }}
                          title={`#${pointNumber}: ${typeConfig?.label} (${point.severity})${point.notes ? `: ${point.notes}` : ''}`}
                        >
                          {pointNumber}
                        </div>
                      );
                    })}


                  </div>

                  {/* Damage Points Summary List */}
                  {damagePoints.length > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <h4 className="text-sm sm:text-md font-medium mb-3">Damage Points Summary</h4>
                      <div className="space-y-2 max-h-40 sm:max-h-32 overflow-y-auto">
                        {damagePoints.map((point, index) => {
                          const typeConfig = DAMAGE_TYPES.find(type => type.value === point.damageType);
                          const severityConfig = SEVERITY_LEVELS.find(level => level.value === point.severity);
                          const pointNumber = index + 1;
                          const isSelected = selectedPointId === point.id;
                          
                          return (
                            <div
                              key={point.id}
                              className={`flex items-start space-x-3 p-3 sm:p-2 rounded-lg cursor-pointer transition-colors touch-manipulation ${
                                isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                              onClick={() => setSelectedPointId(point.id)}
                            >
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold border border-gray-600 flex-shrink-0"
                                style={{ backgroundColor: typeConfig?.color || '#a0a0a0' }}
                              >
                                {pointNumber}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                                  <span className="font-medium text-sm">{typeConfig?.label}</span>
                                  <span className={`px-2 py-1 rounded text-xs font-medium inline-block ${
                                    point.severity === 'minor' ? 'bg-yellow-100 text-yellow-800' :
                                    point.severity === 'moderate' ? 'bg-orange-100 text-orange-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {severityConfig?.label}
                                  </span>
                                </div>
                                {point.notes && (
                                  <p className="text-xs text-gray-600 mt-1 line-clamp-2 sm:truncate" title={point.notes}>
                                    {point.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Damage Point Details & Vehicle Info */}
              <div className="space-y-4 sm:space-y-6">
                
                {/* Selected Damage Point Details - Now appears first for better mobile UX */}
                {selectedPoint && (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base sm:text-lg font-medium">Edit Damage Point</h3>
                      <button
                        onClick={() => removeDamagePoint(selectedPoint.id)}
                        className="text-red-600 hover:text-red-800 transition-colors p-2 touch-manipulation"
                        title="Remove damage point"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Damage Type</label>
                        <select
                          value={selectedPoint.damageType}
                          onChange={(e) => updateDamagePoint(selectedPoint.id, { 
                            damageType: e.target.value as DamagePoint['damageType'] 
                          })}
                          className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                        >
                          {DAMAGE_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                        <select
                          value={selectedPoint.severity}
                          onChange={(e) => updateDamagePoint(selectedPoint.id, { 
                            severity: e.target.value as DamagePoint['severity'] 
                          })}
                          className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                        >
                          {SEVERITY_LEVELS.map((level) => (
                            <option key={level.value} value={level.value}>
                              {level.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          value={selectedPoint.notes}
                          onChange={(e) => updateDamagePoint(selectedPoint.id, { notes: e.target.value })}
                          className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                          rows={3}
                          placeholder="Describe the damage in detail..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Vehicle Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-base sm:text-lg font-medium mb-4">Vehicle Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                      <input
                        type="text"
                        value={vehicleDetails.make}
                        onChange={(e) => setVehicleDetails(prev => ({ ...prev, make: e.target.value }))}
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                        placeholder="e.g., Toyota"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <input
                        type="text"
                        value={vehicleDetails.model}
                        onChange={(e) => setVehicleDetails(prev => ({ ...prev, model: e.target.value }))}
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                        placeholder="e.g., Camry"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                        <input
                          type="text"
                          value={vehicleDetails.year}
                          onChange={(e) => setVehicleDetails(prev => ({ ...prev, year: e.target.value }))}
                          className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                          placeholder="2020"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                        <input
                          type="text"
                          value={vehicleDetails.color}
                          onChange={(e) => setVehicleDetails(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                          placeholder="Blue"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">License Plate</label>
                      <input
                        type="text"
                        value={vehicleDetails.licensePlate}
                        onChange={(e) => setVehicleDetails(prev => ({ ...prev, licensePlate: e.target.value }))}
                        className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                        placeholder="ABC-1234"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                  <textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm"
                    rows={4}
                    placeholder="General notes about vehicle condition..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 border-t bg-gray-50 space-y-3 sm:space-y-0">
          <div className="text-sm text-gray-600 text-center sm:text-left">
            {damagePoints.length} damage point{damagePoints.length !== 1 ? 's' : ''} documented
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-base sm:text-sm touch-manipulation"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base sm:text-sm touch-manipulation"
            >
              <Save className="h-4 w-4" />
              <span>{isSaving ? 'Saving...' : 'Save Report'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Always use Portal to render modal at document body level to avoid DOM nesting issues
  console.log('🚗 [DAMAGE MODAL] Using Portal rendering for all environments');
  return createPortal(modalContent, document.body);
} 